/**
 * KK-527 · Karantina kararı.
 *
 * Karantinadaki mal, karar verilmeden SATIŞA DÖNEMEZ. Üç karardan biri
 * verilir: sağlama al, imhaya ayır, tedarikçiye iade. Bu ekran o kararın
 * verildiği tek yerdir ve "ne olduğu belirsiz mal" birikmesini engeller.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Uyari,
} from "@kantin/ui";
import { StokDurumu } from "@kantin/shared";
import { apiOku, apiYaz } from "./api.js";
import { useTedarikciler } from "./kancalar.js";
import {
  AramaKutusu,
  IslemDurumu,
  MetinAlani,
  Sayfa,
  SayfaBasligi,
  SorguDurumu,
  miktarGecerliMi,
  miktarGoster,
  miktarNormalize,
} from "./ortak.js";

interface StokDagilimi {
  nokta_id: string;
  nokta_ad: string;
  nokta_tur: number;
  depo_mu: boolean;
  durum: number;
  durum_adi: string;
  miktar: string;
}

interface StokUrunu {
  urun_id: string;
  urun_ad: string;
  toplam: string;
  kondisyon: Record<string, string>;
  dagilim: StokDagilimi[];
}

type Karar = "saglama_al" | "imhaya_ayir" | "tedarikciye_iade";

const KARAR_ADI: Record<Karar, string> = {
  saglama_al: "Sağlama al",
  imhaya_ayir: "İmhaya ayır",
  tedarikciye_iade: "Tedarikçiye iade",
};

const KARAR_ACIKLAMASI: Record<Karar, string> = {
  saglama_al:
    "Mal sağlam kondisyona döner ve tekrar satılabilir hale gelir (indirimli satış veya yeniden dağıtım).",
  imhaya_ayir:
    "Mal imha bekliyor kondisyonuna geçer. İmha ayrı ve onaylı bir işlemdir; maliyeti fire/imha gideri olarak yazılır.",
  tedarikciye_iade:
    "Mal tedarikçi iade kondisyonuna geçer. Tedarikçiye gönderildiğinde cari hesaba alacak kaydı düşer ve bir sonraki fatura ile mahsuplaşır.",
};

export function Karantina() {
  const istemci = useQueryClient();
  const [arama, setArama] = useState("");
  const [secili, setSecili] = useState<{
    urunId: string;
    urunAd: string;
    noktaId: string;
    noktaAd: string;
    miktar: string;
  } | null>(null);

  const sorgu = useQuery({
    queryKey: ["stok", "karantina", arama],
    queryFn: () =>
      apiOku<{ urunler: StokUrunu[] }>("/stok", {
        durum: StokDurumu.KARANTINA,
        arama: arama.trim() || undefined,
      }),
  });

  // Sunucu ürün bazında gruplar; karar NOKTA bazında verilir, bu yüzden
  // dağılım satırlarını düzleştiriyoruz.
  const satirlar = (sorgu.data?.urunler ?? []).flatMap((u) =>
    u.dagilim
      .filter((d) => d.durum === StokDurumu.KARANTINA && d.miktar !== "0.000")
      .map((d) => ({
        urunId: u.urun_id,
        urunAd: u.urun_ad,
        noktaId: d.nokta_id,
        noktaAd: d.nokta_ad,
        depoMu: d.depo_mu,
        miktar: d.miktar,
      })),
  );

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Karantina"
        aciklama="Tarihi yaklaşan veya kondisyonu şüpheli mallar. Karar verilene kadar bu mal satışa dönmez."
      />

      <Uyari tur="uyari" baslik="Karar verilmeden mal satışa DÖNEMEZ">
        <p className="m-0">
          Karantinadaki stok, düşük stok uyarılarında ve kasadaki satılabilir
          stokta <strong>sayılmaz</strong>. Üç karardan biri verilene kadar
          burada bekler.
        </p>
      </Uyari>

      <Kart
        baslik="Karantinadaki mallar"
        ustBilgi={
          <span className="text-sm text-murekkep-3">
            <span className="rakam font-mono">{satirlar.length}</span> kalem
          </span>
        }
      >
        <AramaKutusu etiket="Ürün ara" ipucu="Ürün adı veya barkod" deger={arama} degistir={setArama} />
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={satirlar.length === 0}
          bosBaslik={arama ? "Aramaya uygun mal yok" : "Karantinada mal yok"}
          bosAciklama={arama ? "Ürün adını veya tam barkodu kontrol edin." : "Tarihi yaklaşan iade geldiğinde veya kondisyon değişiminde mallar burada listelenir."}
          yenile={() => void sorgu.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">Ürün</th>
              <th scope="col">Nokta</th>
              <th scope="col">Miktar</th>
              <th scope="col">Durum</th>
              <th scope="col">
                <span className="gizli-erisilebilir">Karar</span>
              </th>
            </TabloBaslik>
            <TabloGovde>
              {satirlar.map((s) => (
                <tr key={`${s.urunId}-${s.noktaId}`}>
                  <td className="font-semibold text-murekkep">{s.urunAd}</td>
                  <td className="text-sm text-murekkep-2">
                    {s.noktaAd}
                    {s.depoMu && (
                      <span className="ml-1 text-xs text-murekkep-3">
                        (depo)
                      </span>
                    )}
                  </td>
                  <td className="rakam text-right font-mono">
                    {miktarGoster(s.miktar)}
                  </td>
                  <td>
                    <Rozet tur="uyari">karantina</Rozet>
                  </td>
                  <td className="text-right">
                    <Buton
                      tur="birincil"
                      onClick={() =>
                        setSecili({
                          urunId: s.urunId,
                          urunAd: s.urunAd,
                          noktaId: s.noktaId,
                          noktaAd: s.noktaAd,
                          miktar: s.miktar,
                        })
                      }
                    >
                      Karar ver
                    </Buton>
                  </td>
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>
      </Kart>

      {secili && (
        <KararFormu
          kalem={secili}
          kapat={() => setSecili(null)}
          tamamlandi={() => {
            setSecili(null);
            void istemci.invalidateQueries({ queryKey: ["stok"] });
          }}
        />
      )}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function KararFormu({
  kalem,
  kapat,
  tamamlandi,
}: {
  kalem: {
    urunId: string;
    urunAd: string;
    noktaId: string;
    noktaAd: string;
    miktar: string;
  };
  kapat: () => void;
  tamamlandi: () => void;
}) {
  const [karar, setKarar] = useState<Karar>("saglama_al");
  const [miktar, setMiktar] = useState(kalem.miktar);
  const [tedarikciId, setTedarikciId] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [gonderildi, setGonderildi] = useState(false);
  const tedarikciler = useTedarikciler();

  const uygula = useMutation({
    mutationFn: () =>
      apiYaz("POST", "/stok/karantina-karar", {
        nokta_id: kalem.noktaId,
        urun_id: kalem.urunId,
        miktar: miktarNormalize(miktar),
        karar,
        ...(karar === "tedarikciye_iade" ? { tedarikci_id: tedarikciId } : {}),
        ...(aciklama.trim() ? { aciklama: aciklama.trim() } : {}),
      }),
    onSuccess: tamamlandi,
  });

  const miktarHatasi =
    gonderildi && !miktarGecerliMi(miktar)
      ? "Miktarı 5 veya 5,500 biçiminde girin."
      : undefined;
  const tedarikciHatasi =
    gonderildi && karar === "tedarikciye_iade" && !tedarikciId
      ? "Tedarikçiye iade için tedarikçi seçilmelidir."
      : undefined;

  return (
    <Kart baslik={`Karar: ${kalem.urunAd} · ${kalem.noktaAd}`}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!miktarGecerliMi(miktar)) return;
          if (karar === "tedarikciye_iade" && !tedarikciId) return;
          uygula.mutate();
        }}
      >
        <p className="m-0 text-sm text-murekkep-2">
          Karantinada bekleyen miktar:{" "}
          <strong className="rakam font-mono">
            {miktarGoster(kalem.miktar)}
          </strong>
          . Kısmi karar verebilirsiniz; kalan miktar karantinada bekler.
        </p>

        <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
          <legend className="mb-1 p-0 text-sm font-semibold text-murekkep-2">
            Karar
          </legend>
          {(Object.keys(KARAR_ADI) as Karar[]).map((k) => (
            <label
              key={k}
              className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 ${
                karar === k
                  ? "border-simit bg-simit-yumusak"
                  : "border-cetvel bg-yuzey"
              }`}
            >
              <input
                type="radio"
                name="karantina-karar"
                checked={karar === k}
                onChange={() => setKarar(k)}
                className="mt-1 h-4 w-4 accent-simit"
              />
              <span>
                <span className="block font-semibold text-murekkep">
                  {KARAR_ADI[k]}
                </span>
                <span className="block text-sm text-murekkep-2">
                  {KARAR_ACIKLAMASI[k]}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        <div className="grid gap-3 md:grid-cols-2">
          <Alan
            etiket="Karara konu miktar"
            value={miktar}
            onChange={(e) => setMiktar(e.target.value)}
            inputMode="decimal"
            {...(miktarHatasi ? { hata: miktarHatasi } : {})}
          />
          {karar === "tedarikciye_iade" && (
            <Secim
              etiket="Tedarikçi"
              value={tedarikciId}
              onChange={(e) => setTedarikciId(e.target.value)}
              secenekler={[
                { deger: "", metin: "Seçiniz" },
                ...(tedarikciler.data?.tedarikciler ?? []).map((t) => ({
                  deger: t.id,
                  metin: t.ad,
                })),
              ]}
            />
          )}
        </div>

        {tedarikciHatasi && (
          <p className="m-0 text-xs text-hata" role="alert">
            {tedarikciHatasi}
          </p>
        )}

        <MetinAlani
          etiket="Gerekçe / not"
          value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
          rows={2}
          ipucu="Karar denetim kaydına geçer; sonradan 'kim, neden' sorusunun cevabı budur."
        />

        <IslemDurumu hata={uygula.error} />

        <div className="flex flex-wrap gap-2">
          <Buton type="submit" tur="birincil" yukleniyor={uygula.isPending}>
            Kararı uygula
          </Buton>
          <Buton type="button" tur="ikincil" onClick={kapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </Kart>
  );
}
